import { Clock } from "lucide-react"
import { useSkyClock } from "@/components/SkyClock"
import { personalInfo, socialLinks } from "@/config/personal"
import { BitBoyHead } from "@/components/BitBoyHead"

// ============================================================================
// Sub-Components
// ============================================================================

function BioSection() {
  return (
    <section className="mb-10">
      {/* Head + name + date */}
      <div className="mb-6 flex items-center gap-3">
        <BitBoyHead />
        <div className="flex flex-col">
          <span className="text-lg font-medium whitespace-nowrap text-fg">
            {personalInfo.name}
          </span>
          <span className="text-xs text-fg-faint">
            Updated {personalInfo.updatedDate}
          </span>
        </div>
      </div>

      {/* Bio paragraphs */}
      <div className="space-y-3 text-body-sm text-fg-muted">
        {personalInfo.bio.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        <p>
          Currently working on{" "}
          <a
            href="https://bduck.us"
            target="_blank"
            rel="noopener noreferrer"
            className="link font-medium"
          >
            bduck
          </a>
          , a GitHub app that reviews pull requests with a team of models, and{" "}
          <a
            href="https://findskills.cc"
            target="_blank"
            rel="noopener noreferrer"
            className="link font-medium"
          >
            FindSkills
          </a>
          , a cloud profile for the agent skills you trust, loaded only when a
          task needs them.
        </p>
        <p>
          For fun, I also made{" "}
          <a
            href="https://backrooms.justarr.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="link font-medium"
          >
            Backrooms
          </a>
          {" "}and{" "}
          <a
            href="https://open-arena.net"
            target="_blank"
            rel="noopener noreferrer"
            className="link font-medium"
          >
            Open Arena
          </a>
          , a site about card games.
        </p>
      </div>
    </section>
  )
}

function Footer() {
  const { now } = useSkyClock()
  const time = now().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: personalInfo.timeZone,
  })

  return (
    <footer className="mt-12">
      <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        {socialLinks.map((link) => (
          <a
            key={link.name}
            href={link.url}
            target={link.isExternal ? "_blank" : undefined}
            rel={link.isExternal ? "noopener noreferrer" : undefined}
            className="flex items-center gap-1.5 text-xs text-fg-faint transition-colors hover:text-fg-body"
          >
            <link.icon size={14} strokeWidth={1.5} />
            <span>{link.name}</span>
          </a>
        ))}
      </div>
      <p className="flex items-center gap-1 text-xs text-fg-faint tabular-nums">
        <Clock size={12} strokeWidth={1.5} />
        {time} in {personalInfo.location}
      </p>
    </footer>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function HomePage() {
  return (
    <>
      <BioSection />
      <Footer />
    </>
  )
}
